import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  Fingerprint,
  IdCard,
  LockKeyhole,
  ScanLine,
  ShieldCheck,
  Smartphone,
  UserRound,
  WalletCards,
  X,
  XCircle,
} from 'lucide-react';
import { api, dateOnly, dateTime } from '../lib/api';

const req = (method, body = {}) => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

const titleCase = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());
const initials = value => String(value || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '?';

function credentialLabel(row) {
  if (row?.credential_label) return row.credential_label;
  return row?.relationship_source === 'companies_house'
    ? 'Corporate Officer Identification'
    : 'Staff Identification';
}

export default function CompanyIDHolder() {
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [qr, setQr] = useState('');
  const [presentation, setPresentation] = useState(null);
  const [revealedCode, setRevealedCode] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);

  const credentials = data?.credentials || [];
  const selected = useMemo(
    () => credentials.find(row => row.id === selectedId) || credentials.find(row => row.status === 'active') || credentials[0] || null,
    [credentials, selectedId],
  );

  const load = async () => {
    setLoading(true);
    try {
      const result = await api('/api/company-id/me');
      setData(result);
      setSelectedId(current => current || result?.credentials?.find(row => row.status === 'active')?.id || result?.credentials?.[0]?.id || '');
      setNotice('');
    } catch (error) {
      setNotice(error.message || 'Your Company ID could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setQr('');
    setPresentation(null);
    setRevealedCode('');
    if (!selected?.id || selected.status !== 'active') return undefined;

    let cancelled = false;
    let timer = null;

    const refresh = async () => {
      try {
        const next = await api(`/api/company-id/me/${encodeURIComponent(selected.id)}/presentation`, req('POST'));
        if (cancelled) return;
        setPresentation(next);
        const image = await QRCode.toDataURL(next.verification_url, {
          width: 320,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (!cancelled) setQr(image);
      } catch (error) {
        if (!cancelled) {
          setQr('');
          setPresentation(null);
          setNotice(error.message || 'Live verification is not available for this credential.');
        }
      }
    };

    refresh();
    timer = window.setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [selected?.id, selected?.status]);

  const reveal = async () => {
    if (!selected?.id || !selected.personal_code_stored) return;
    if (revealedCode) {
      setRevealedCode('');
      return;
    }
    try {
      const result = await api(`/api/company-id/me/${encodeURIComponent(selected.id)}/reveal-personal-code`, req('POST'));
      setRevealedCode(result.personal_code || '');
      window.setTimeout(() => setRevealedCode(''), 60000);
    } catch (error) {
      setNotice(error.message || 'The personal code could not be revealed.');
    }
  };

  const addToAppleWallet = async () => {
    if (!selected?.id) return;
    if (!data?.wallet?.apple?.configured) {
      setNotice('Apple Wallet is not configured for Company ID yet.');
      return;
    }
    setWalletBusy(true);
    setNotice('');
    try {
      let appleWalletUrl = selected?.wallet?.apple_wallet_url || '';
      if (!appleWalletUrl) {
        const result = await api(`/api/company-id/me/${encodeURIComponent(selected.id)}/wallet/apple`, req('POST'));
        appleWalletUrl = result?.wallet?.apple_wallet_url || '';
        if (result?.wallet) {
          setData(current => ({
            ...current,
            credentials: (current?.credentials || []).map(row => row.id === selected.id
              ? { ...row, wallet: { ...(row.wallet || {}), ...result.wallet } }
              : row),
          }));
        }
      }
      if (!appleWalletUrl) throw new Error('Pass Studio did not return an Apple Wallet link.');
      window.location.assign(appleWalletUrl);
    } catch (error) {
      setNotice(error.message || 'Your Company ID could not be prepared for Apple Wallet.');
    } finally {
      setWalletBusy(false);
    }
  };

  const copy = async value => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice('Copied to clipboard.');
    } catch {
      setNotice('Your browser did not allow copying.');
    }
  };

  if (loading) return <main className="cid-present-page cid-formal-page">
    <div className="cid-present-loading"><div className="cid-spinner"/><strong>Opening your Company ID</strong><span>Loading your live company-issued credential…</span></div>
  </main>;

  if (!credentials.length) return <main className="cid-present-page cid-formal-page">
    <div className="cid-present-empty">
      <div className="cid-holder-empty-icon"><IdCard size={34}/></div>
      <span>SOUSA MURRAY COMPANY ID</span>
      <h1 className="govuk-heading-xl">No Company ID has been issued yet</h1>
      <p className="govuk-body">Your permanent Staff ID number is created when an authorised administrator issues your first Company ID credential.</p>
      <button className="cid-present-back" onClick={() => { window.location.href = '/'; }}><ArrowLeft size={16}/>Back to Customer Operations</button>
    </div>
  </main>;

  const relationshipCurrent = ['verified', 'employer_verified'].includes(selected.relationship_status);
  const staffState = String(selected.staff_status || data?.holder?.status || 'active').toLowerCase();
  const staffCurrent = !['deleted', 'inactive', 'terminated', 'former', 'offboarded', 'disabled', 'suspended'].includes(staffState);
  const expired = Boolean(selected.expires_at && Date.parse(selected.expires_at) <= Date.now());
  const valid = selected.status === 'active' && relationshipCurrent && staffCurrent && !expired;
  const personalCode = revealedCode || selected.personal_code_masked || 'Not stored';
  const statusText = valid ? 'VALID' : expired ? 'EXPIRED' : titleCase(selected.status);
  const role = selected.internal_title || titleCase(selected.companies_house_role) || 'Staff member';
  const staffId = selected.staff_id_number || data?.identity?.staff_id_number || '—';

  return <main className="cid-present-page cid-formal-page">
    <header className="cid-present-topbar cid-formal-topbar">
      <button className="cid-present-icon-button" onClick={() => { window.location.href = '/'; }} aria-label="Back to Customer Operations"><ArrowLeft size={20}/></button>
      <div className="cid-present-topbrand"><Fingerprint size={21}/><div><strong>SOUSA MURRAY</strong><span>COMPANY ID</span></div></div>
      <div className={`cid-present-state ${valid ? 'valid' : 'invalid'}`}>{valid ? <CheckCircle2 size={15}/> : <XCircle size={15}/>} {statusText}</div>
    </header>

    {notice && <div className="cid-present-notice">{notice}</div>}

    {credentials.length > 1 && <label className="cid-present-switch govuk-label">
      <select className="govuk-select" value={selected.id} onChange={event => setSelectedId(event.target.value)}>
        {credentials.map(row => <option key={row.id} value={row.id}>{row.company_name} · {row.staff_id_number || row.credential_number}</option>)}
      </select>
      <ChevronDown size={16}/>
    </label>}

    <section className={`cid-formal-credential status-${selected.status} ${valid ? 'is-valid' : 'is-invalid'}`} aria-label="Sousa Murray Company ID">
      <div className="cid-formal-watermark" aria-hidden="true">SM</div>

      <header className="cid-formal-card-head">
        <div className="cid-formal-issuer">
          <div className="cid-formal-mark"><Fingerprint size={20}/></div>
          <div>
            <strong>{selected.company_name || 'SOUSA MURRAY'}</strong>
            <span>COMPANY IDENTIFICATION CREDENTIAL</span>
          </div>
        </div>
        <div className={`cid-formal-validity ${valid ? 'valid' : 'invalid'}`}>
          {valid ? <BadgeCheck size={17}/> : <XCircle size={17}/>}
          <span>{statusText}</span>
        </div>
      </header>

      <div className="cid-formal-card-body">
        <div className="cid-formal-photo">
          {selected.photo_url
            ? <img src={selected.photo_url} alt="Credential holder"/>
            : <div className="cid-formal-photo-missing"><UserRound size={34}/><span>PHOTO<br/>NOT ON FILE</span></div>}
        </div>

        <div className="cid-formal-identity">
          <span className="cid-formal-type">{credentialLabel(selected)}</span>
          <h1 className="govuk-heading-xl">{selected.legal_name}</h1>
          <p className="govuk-body">{role}</p>

          <div className="cid-formal-staff-id">
            <span>STAFF ID</span>
            <strong>{staffId}</strong>
          </div>

          <div className="cid-formal-authority">
            <ShieldCheck size={15}/>
            <span>{selected.relationship_source === 'employer'
              ? 'Authority: current employer staff record'
              : `Authority: Companies House · ${titleCase(selected.companies_house_role) || 'Officer'}`}</span>
          </div>
        </div>

        <div className="cid-formal-qr">
          {qr ? <img src={qr} alt="Live Company ID verification QR code"/> : <div className="cid-formal-qr-wait"><ScanLine size={44}/></div>}
          <strong>LIVE VERIFY</strong>
          <span>{presentation?.expires_at ? 'Scan this code' : selected.status === 'active' ? 'Preparing…' : 'Unavailable'}</span>
        </div>
      </div>

      <div className="cid-formal-company-row">
        <Building2 size={16}/>
        <div><span>ISSUING LEGAL ENTITY</span><strong>{selected.company_name}</strong></div>
        <span className="cid-formal-company-number">Company No. {selected.company_number}</span>
      </div>

      <div className="cid-formal-meta">
        <div><span>Credential serial</span><strong>{selected.credential_number}</strong></div>
        <div><span>Issued</span><strong>{dateOnly(selected.issued_at)}</strong></div>
        <div><span>Valid until</span><strong>{selected.expires_at ? dateOnly(selected.expires_at) : 'Until revoked'}</strong></div>
        <div><span>Standard</span><strong>{selected.credential_format || 'SMCID-1'}</strong></div>
      </div>

      <footer className="cid-formal-card-foot">
        <span>Employer-issued company credential · Not government identification</span>
        <strong>Verify live before relying on this credential</strong>
      </footer>
    </section>

    <div className="cid-formal-live-note">
      <ShieldCheck size={17}/>
      <div>
        <strong>Live credential</strong>
        <span>The QR is short-lived and checks the current staff relationship, company status and credential status. Do not rely on screenshots.</span>
        {presentation?.expires_at && <small>Current verification presentation expires {dateTime(presentation.expires_at)}</small>}
      </div>
    </div>

    {selected.persistent_verification_url && <div className="cid-formal-persistent-link">
      <div><BadgeCheck size={17}/><span><strong>Permanent verification link</strong><small>Use this link in an authorised staff directory or company email signature. It always performs a fresh live status check and does not expose restricted credential data.</small></span></div>
      <button onClick={() => copy(selected.persistent_verification_url)}><Copy size={15}/>Copy verification link</button>
    </div>}

    <div className="cid-present-actions cid-formal-actions">
      <button onClick={() => setDetailsOpen(true)}><ShieldCheck size={17}/>Credential details</button>
      <button
        onClick={addToAppleWallet}
        disabled={!valid || !data?.wallet?.apple?.configured || walletBusy}
        title={!data?.wallet?.apple?.configured
          ? 'Pass Studio must be connected by an authorised administrator before Apple Wallet can be used.'
          : !valid
            ? 'Only a currently valid Company ID can be added to Apple Wallet.'
            : 'Add this company-issued credential to Apple Wallet via Pass Studio.'}
      ><WalletCards size={17}/>{walletBusy ? 'Preparing Apple Wallet…' : selected?.wallet?.issued ? 'Open in Apple Wallet' : 'Add to Apple Wallet'}</button>
    </div>

    {detailsOpen && <div className="cid-present-details-backdrop" onMouseDown={event => { if (event.currentTarget === event.target) setDetailsOpen(false); }}>
      <section className="cid-present-detailsview" role="dialog" aria-modal="true" aria-label="Company ID details">
        <div className="cid-present-details-top">
          <div><span>FORMAL CREDENTIAL RECORD</span><h2 className="govuk-heading-m">{selected.legal_name}</h2></div>
          <button onClick={() => setDetailsOpen(false)} aria-label="Close"><X size={20}/></button>
        </div>

        <div className="cid-present-detail-grid">
          <Detail label="Credential type" value={credentialLabel(selected)}/>
          <Detail label="Credential standard" value={selected.credential_format || 'SMCID-1'} mono/>
          <Detail label="Staff ID" value={staffId} mono good/>
          <Detail label="Holder photograph" value={selected.holder_photo_present || selected.photo_url ? 'On file' : 'Not on file'} good={Boolean(selected.holder_photo_present || selected.photo_url)}/>
          <Detail label="Identity assurance" value={selected.identity_assurance === 'company_checked' ? 'Company checked' : 'Legacy — not recorded'} good={selected.identity_assurance === 'company_checked'}/>
          <Detail label="Identity check method" value={selected.identity_assurance_method ? titleCase(selected.identity_assurance_method) : 'Not recorded'}/>
          <Detail label="Identity checked by" value={selected.identity_checked_by_name || 'Not recorded'}/>
          <Detail label="Issuing company" value={selected.company_name}/>
          <Detail label="Company number" value={selected.company_number} mono/>
          <Detail label="Company title" value={role}/>
          <Detail label="Authority source" value={selected.relationship_source === 'employer' ? 'Sousa Murray employer record' : 'Companies House'}/>
          <Detail label="Relationship" value={titleCase(selected.relationship_status)} good={relationshipCurrent}/>
          <Detail label="Staff relationship" value={staffCurrent ? 'Current' : titleCase(staffState)} good={staffCurrent}/>
          <Detail label="Credential serial" value={selected.credential_number} mono/>
          <Detail label="Issued" value={dateOnly(selected.issued_at)}/>
          <Detail label="Issued by" value={selected.issued_by_name || 'Authorised Company ID administrator'}/>
          <Detail label="Expiry" value={selected.expires_at ? dateOnly(selected.expires_at) : 'Until revoked'}/>
        </div>

        <div className="cid-present-scope">
          <IdCard size={20}/>
          <div><strong>Credential scope</strong><span>This credential identifies the named holder's current relationship with the issuing company. It is company-issued identification and is not a passport, driving licence or other government identity document.</span></div>
        </div>

        <div className="cid-present-secret">
          <div className="cid-present-secret-icon"><LockKeyhole size={20}/></div>
          <div className="cid-present-secret-copy"><span>COMPANIES HOUSE PERSONAL CODE</span><code>{personalCode}</code><small>Restricted credential data. Never included in the public verifier.</small></div>
          {selected.personal_code_stored && <div className="cid-present-secret-actions">
            <button onClick={reveal}>{revealedCode ? <EyeOff size={16}/> : <Eye size={16}/>} {revealedCode ? 'Hide' : 'Reveal'}</button>
            {revealedCode && <button onClick={() => copy(revealedCode)}><Copy size={16}/>Copy</button>}
          </div>}
        </div>

        <div className="cid-present-wallet-row">
          <div><Smartphone size={20}/><span><strong>Apple Wallet</strong><small>{selected?.wallet?.issued ? 'Issued through Pass Studio' : data?.wallet?.apple?.configured ? 'Ready through Pass Studio' : 'Pass Studio setup required'}</small></span></div>
          <div><WalletCards size={20}/><span><strong>Google Wallet</strong><small>Not enabled for Company ID</small></span></div>
        </div>
      </section>
    </div>}
  </main>;
}

function Detail({ label, value, mono, good }) {
  return <div className="cid-present-detail"><span>{label}</span><strong className={`${mono ? 'mono' : ''} ${good ? 'good' : ''}`}>{value || '—'}</strong></div>;
}
